<template>
    <section :id="'inverter' + item">
        <SectionHeader
            :heading="heading"
            :completed="completed"
            :to-complete="toComplete"
        />

        <SectionGroup
            :id="'inverterPCE' + item"
            heading="Inverter"
            :validated.sync="validated.inverter"
        >
            <template #default="{ validate }">
                <InputField label="Manufacturer" name="manufacturer" type="text" required readonly :value.sync="data.productManufacturer" />
                <InputField label="Model" name="model" type="text" required readonly :value.sync="data.productModelNumber" />
                <InputField label="Max DC input" name="maxDCWatts" type="text" required readonly :value.sync="data.maxDCWatts" unit="W" />
                <InputField label="Number of MPPT trackers" name="numberOfMPPTrackers" type="text" required readonly :value.sync="data.numberOfMPPTrackers" />
                <InputField v-if="data.limitationDeviceLocation" label="Export Limitation Device Location" name="limitationDeviceLocation" type="text" readonly :value.sync="data.limitationDeviceLocation" />
                <InputField v-if="data.limitedTo" label="Limited to (W)" name="limitedTo" type="text" required readonly :value.sync="data.limitedTo" unit="W" />

                <template v-for="(n, index) in Number(data.numberOfMPPTrackers)">
                    <InputField :key="index + 1" :label="'Max short circuit current - MPPT' + n" name="maxShortCircuitCurrent" type="text" required :value.sync="data.maxShortCircuitCurrent[index]" unit="A" />
                    <InputField :key="index + 100" :label="'Max input current - MPPT' + n" name="maxInputCurrent" type="text" required :value.sync="data.maxInputCurrent[index]" unit="A" />
                </template>

                <CheckboxGroup required>
                    <CheckboxField label="The inverter is installed to manufacturer’s instructions" :value.sync="data.inverterInstalledToInstructions" @validate="validate" />
                    <CheckboxField label="The AC isolator (if required) is mounted adjacent to the inverter and is correctly rated and lockable" :value.sync="data.acIsolatorCorrect" @validate="validate" />
                    <CheckboxField label="The AC circuit breaker is mounted in the switchboard and is correctly rated and lockable" :value.sync="data.acCircuitCorrect" @validate="validate" />
                    <CheckboxField label="The AC cable voltage rise from inverter terminals to the point of supply is less than 2%" :value.sync="data.acCableVoltageWithinLimit" @validate="validate" />
                </CheckboxGroup>

                <template v-for="(n, index) in data.voltageRiseData">
                    <div :key="index" class="field field--full required">
                        <label>AC cable voltage rise from inverter terminals to the point of supply (L{{ Number(data.voltageRiseData[index].phase) + 1 }})</label>
                    </div>

                    <InputField :key="index + 1" name="dataVolt" type="number" readonly step=".01" required :value="decimals(data.voltageRiseData[index].volt)" unit="V" @validate="validate" />
                    <InputField :key="index + 2" name="dataPercent" type="number" readonly step=".01" required :value="decimals(data.voltageRiseData[index].percent)" unit="%" @validate="validate" />
                </template>

                <InputField label="AC voltage at inverter terminals under load" name="acVRiseLoad" type="number" required :value.sync="data.acVRiseLoad" unit="V" @validate="validate" />
                <InputField label="AC voltage at inverter terminals under no-load" name="acVRiseNoLoad" type="number" required :value.sync="data.acVRiseNoLoad" unit="V" @validate="validate" />
            </template>
        </SectionGroup>


        <SectionGroup
            :id="'pvModuleSolarPanel' + item"
            heading="PV Module"
            :validated.sync="validated.pvModule"
        >
            <template #default="{ validate }">
                <InputField label="Manufacturer" name="panelManifacturer" type="string" required readonly :value.sync="data.panelManufacturer" @validate="validate" />
                <InputField label="Model" name="panelModel" type="string" required readonly :value.sync="data.panelModel" @validate="validate" />
                <InputField label="Connector manufacturer" name="panelDCManifacturer" type="string" required :value.sync="data.panelDCManifacturer" @validate="validate" />
                <InputField label="Connector type/model" name="panelDCModel" type="string" required :value.sync="data.panelDCModel" @validate="validate" />

                <div v-for="(string, index) in data.strings" :key="index" class="fields__group-quarter mt-lg">
                    <div class="commissioning-sheet__tag small semi"><span>String {{ index + 1 }}</span></div>
                    <div>
                        <InputField label="MPPT" name="mppt" type="string" required readonly :value.sync="string.mppt" />
                        <InputField label="# of PV modules" name="panels" type="string" required readonly :value.sync="string.panels" />
                        <InputField label="Azimuth" name="azimuth" type="string" required readonly :value.sync="string.azimuth" />
                        <InputField label="Tilt" name="tilt" type="string" required readonly :value.sync="string.tilt" />
                    </div>
                </div>

                <InputField label="String fuse voltage rating if installed" name="stringFuseVolt" type="text" :value.sync="data.stringFuseVolt" unit="V" @validate="validate" />
                <InputField label="String fuse current rating if installed" name="stringFuseCurrent" type="text" :value.sync="data.stringFuseCurrent" unit="A" @validate="validate" />
                <InputField label="PV array maximum voltage – calculated for lowest operating temperature" name="pvMaxVolt" type="text" required :value.sync="data.pvMaxVolt" unit="V" @validate="validate" />
                <InputField label=" PV array short circuit current – calculated as the sum of all the array currents" name="pvShortCircuit" type="text" required :value.sync="data.pvShortCircuit" unit="A" @validate="validate" />
            </template>
        </SectionGroup>

        <SectionGroup
            :id="'dcLoadBreaking' + item"
            heading="DC isolator"
            :validated.sync="validated.dcIsolator"
        >
            <template #default="{ validate }">
                <div class="field--full">
                    <div class="commissioning-sheet__tag small semi"><span>DC isolator</span></div>
                </div>
                <SelectField class="field--full" label="Number of Different DC isolator TYPES" placeholder="Select Option" :options="['0', '1', '2', '3', '4', '5']" name="dcIsolatorsNum" type="number" :value.sync="data.dcIsolatorsNum" />
                <template v-if="data.dcIsolators && data.dcIsolators.length">
                    <template v-for="(_, index) in data.dcIsolators">
                        <InputField :key="'dcManifacturer['+ index +']'" :label="'Manufacturer - DC isolator TYPE ' + (index + 1)" :name="'dcManifacturer['+ index +']'" type="text" required :value.sync="data.dcIsolators[index].dcManifacturer" placeholder="Type here" @validate="validate" />
                        <InputField :key="'dcModel['+ index +']'" :label="'Model - DC isolator TYPE ' + (index + 1)" :name="'dcModel['+ index +']'" type="text" required :value.sync="data.dcIsolators[index].dcModel" placeholder="Type here" @validate="validate" />
                        <InputField :key="'dcQty['+ index +']'" class="field--full" :label="'QUANTITY - DC ISOLATOR TYPE ' + (index + 1)" :name="'dcQty['+ index +']'" type="number" required :value.sync="data.dcIsolators[index].dcQty" placeholder="Type here" @validate="validate" @keydown.native="numericOnly($event)" />
                    </template>
                </template>
                <CheckboxGroup required>
                    <CheckboxField label="All DC isolators are correctly rated and configured for the PV arrays they isolate" :value.sync="data.dcConfirmation" @validate="validate" />
                </CheckboxGroup>
                <div class="field--full mt-lg">
                    <div class="commissioning-sheet__tag small semi"><span>Inverter integrated DC isolator</span></div>
                </div>

                <RadioField
                    class="mb-md"
                    heading="The inverter has an integrated DC isolator?"
                    :options="['Yes', 'Yes, however, an additional adjacent DC isolator will be installed', 'No']"
                    required
                    :value.sync="data.inverterHasDcIsolator" @validate="validate"
                />
                <CheckboxGroup v-if="data.inverterHasDcIsolator === 'Yes'" required>
                    <CheckboxField label="The inverter integrated DC isolator meets all the requirements in the Australian standards" :value.sync="data.dcIsolatorAusStandard" @validate="validate" />
                    <CheckboxField label="A manufacture’s specification (spec) sheet has been provided and included with system documentation" :value.sync="data.dcIsolatorSpecProvided" @validate="validate" />
                    <CheckboxField label="A manufacture’s declaration has been provided and included with system documentation" :value.sync="data.dcIsolatorDeclarationProvided" @validate="validate" />
                </CheckboxGroup>

                <RadioField
                    heading="Does the local state or territory regulator require a physically separate adjacent DC isolator?"
                    :options="['Yes', 'No']"
                    required
                    :value.sync="data.separatedDcIsolatorRequired" @validate="validate"
                />

                <div class="field--full mt-lg">
                    <div class="commissioning-sheet__tag small semi"><span>Installation and testing of DC isolators</span></div>
                </div>
                <CheckboxGroup required>
                    <CheckboxField label="DC isolators enclosures IP maintained e.g. orientation approved, pips installed if supplied" :value.sync="data.dcIsolatorEnclosuresIpMaintained" @validate="validate" />
                    <CheckboxField label="DC isolators installed to manufacturer’s instructions" :value.sync="data.dcIsolatorManifacturerInstructions" @validate="validate" />
                    <CheckboxField label="All DC isolators have been tested (turned off) under load" :value.sync="data.dcIsolatorTestedLoad" @validate="validate" />
                </CheckboxGroup>
            </template>
        </SectionGroup>

        <SectionGroup
            :id="'conduitCabling' + item"
            heading="Conduit and cabling"
            :validated.sync="validated.conduitCabling"
        >
            <template #default="{ validate }">
                <CheckboxGroup required>
                    <CheckboxField label="Conduit compliantly installed and adequately supported e.g. glued, secured and labelled" :value.sync="data.conduitCompliant" @validate="validate" />
                    <CheckboxField label="Roof penetrations for cabling system adequately sealed e.g. appropriate collard flashing for roof material" :value.sync="data.roofPenetrationSealed" @validate="validate" />
                    <CheckboxField label="Cable is mechanically protected and supported as per AS/NZS 3000 and AS/NZS 5033" :value.sync="data.cableProtected" @validate="validate" />
                    <CheckboxField label="DC cable volt drop (Vd) is less than 3%" :value.sync="data.cableVdInLimit" @validate="validate" />
                </CheckboxGroup>

                <InputField label="Dc Cable Volt Drop" name="dcCableVd" type="number" required :value.sync="data.dcCableVd" unit="%" @validate="validate" />
            </template>
        </SectionGroup>

        <SectionGroup
            :id="'mountingSystem' + item"
            heading="Mounting system"
            :validated.sync="validated.mountingSystem"
        >
            <template #default="{ validate }">
                <InputField label="Manufacturer" name="mountingSystemManifacturer" type="text" required :value.sync="data.mountingSystemManifacturer" placeholder="Please type" @validate="validate" />
                <InputField label="Model" name="mountingSystemModel" type="text" required :value.sync="data.mountingSystemModel" placeholder="Please type" @validate="validate" />

                <CheckboxGroup required>
                    <CheckboxField label="The mounting system is installed to manufacturer’s instructions" :value.sync="data.systemManifacturerInstructions" @validate="validate" />
                    <CheckboxField label="The roof penetrations for the mounting system are adequately sealed e.g. tiles maintain their original ingress protection" :value.sync="data.systemRoofPenetrationSealed" @validate="validate" />
                    <CheckboxField label="The mounting system manufacturer’s exclusion zones are adhered to and meet the minimum requirement of AS/NZS 1170.2" :value.sync="data.systemZones" @validate="validate" />
                    <CheckboxField label="The array frame is certified to AS/NZS 1170.2" :value.sync="data.systemArrayFrame" @validate="validate" />

                    <CheckboxField label="Galvanically dissimilar metals are not in contact with each other e.g. separated by nylon or rubber spacers" :value.sync="data.systemNoContactMetals" @validate="validate" />
                    <CheckboxField label="The panels are installed to the panel manufacturer’s instructions" :value.sync="data.systemPanelManifacturerInstructions" @validate="validate" />
                    <CheckboxField label="The panels are installed within the manufacturer’s clamping zones" :value.sync="data.systemPanelManifacturerZones" @validate="validate" />
                    <CheckboxField label="All bolts and terminations are correctly torqued" :value.sync="data.systemBolts" @validate="validate" />
                    <CheckboxField label="Mounting system and panels are correctly earthed" :value.sync="data.systemEarthed" @validate="validate" />
                    <CheckboxField label="Earth connections are UV and mechanically protected e.g. gal sprayed" :value.sync="data.systemEarthConnection" @validate="validate" />
                </CheckboxGroup>

                <InputField label="Earth fault alarm type e.g. visual, audible, electronic, etc." name="mountingEarthFaultAlarm" type="text" required :value.sync="data.mountingEarthFaultAlarm" placeholder="Please type" @validate="validate" />
            </template>
        </SectionGroup>

        <SectionGroup
            :id="'otherGroup' + item"
            heading="Other"
            :validated.sync="validated.other"
        >
            <template #default="{ validate }">
                <CheckboxGroup required>
                    <CheckboxField label="System is labelled as per requirements in AS/NZS 3000" :value.sync="data.systemRequirement3000" @validate="validate" />
                    <CheckboxField label="System is labelled as per requirements in AS/NZS 5033" :value.sync="data.systemRequirement5033" @validate="validate" />
                    <CheckboxField label="System is labelled as per requirements in AS/NZS 4777.1 " :value.sync="data.systemRequirement4777" @validate="validate" />
                    <CheckboxField label="System is labelled as per requirements in the CEC guidelines " :value.sync="data.systemRequirementCEC" @validate="validate" />
                    <CheckboxField label="LV wiring installed by a licensed electrical worker" :value.sync="data.LVbyLicensed" @validate="validate" />
                    <CheckboxField label="LV wiring tested and certified by a licensed electrical worker" :value.sync="data.LVtestedbyLicensed" @validate="validate" />
                    <CheckboxField label="System is compliant as per marking and documentation requirements in AS/NZS 5033" :value.sync="data.systemCompliant" @validate="validate" />
                    <CheckboxField label="Distributed energy resource (DER) documented as per network service provider (NSP) requirements" :value.sync="data.DERdocumented" @validate="validate" />
                    <CheckboxField label="System is labelled as per any local state or territory requirements" :value.sync="data.systemLabelled" @validate="validate" />
                    <CheckboxField label="Inverter remote monitoring" :value.sync="data.inverterRemoteMonitoring" @validate="validate" />
                </CheckboxGroup>

                <div class="field--full mt-lg">
                    <div class="commissioning-sheet__tag small semi"><span>Inverter settings</span></div>
                </div>

                <SelectField label="Region" placeholder="Select Option" :options="['Region A', 'Region B', 'Region C', 'New Zealand']" name="region" :value.sync="data.region" />

                <div class="field--full my-md"></div>

                <SelectField
                    label="Is this inverter export limited?"
                    :options="['No', 'Yes']"
                    name="inverterExportLimited"
                    :value.sync="data.inverterExportLimited"
                />

                <InputField v-if="data.inverterExportLimited === 'Yes'" label="Export Limited To (W)" name="exportLimitSet" type="number" :value.sync="data.exportLimitSet" unit="W" />

                <CheckboxGroup required>
                    <CheckboxField label="Inverter is installed per NSP connection agreement" :value.sync="data.inverterInstalledNSP" @validate="validate" />
                    <CheckboxField label="Country code settings are set to Australia" :value.sync="data.countryCodeSet" @validate="validate" />
                    <CheckboxField label="Inverter shuts down within 2 seconds of isolation" :value.sync="data.inverterShutdownSeconds" @validate="validate" />
                    <CheckboxField label="Inverter takes at least 60 seconds to start after re-connection to supply" :value.sync="data.inverterStartSeconds" @validate="validate" />
                    <CheckboxField label="Inverter then takes 6 minutes to ramp up to 100%" :value.sync="data.inverterRampSeconds" @validate="validate" />
                </CheckboxGroup>
            </template>
        </SectionGroup>
    </section>
</template>
<script>
import InputField from './InputField';
import SelectField from './SelectField';
import CheckboxGroup from './CheckboxGroup';
import CheckboxField from './CheckboxField';
import RadioField from './RadioField';
import SectionHeader from './SectionHeader';
import SectionGroup from './SectionGroup';

export default {
    components: {
        SectionHeader,
        SectionGroup,
        InputField,
        CheckboxGroup,
        CheckboxField,
        RadioField,
        SelectField
    },

    props: {
        heading: {
            type: String,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        item: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            validated: {
                inverter: false,
                pvModule: false,
                dcIsolator: false,
                conduitCabling: false,
                mountingSystem: false,
                other: false,
            },
            toComplete: 6,
        };
    },

    computed: {
        completed() {
            return Object.values(this.validated).filter(item => item === true).length;
        },
    },

    watch: {
        'data.dcIsolatorsNum'(number) {
            this.generateDcIsolators(number);
        },

        completed() {
            this.$emit('update:validated', this.completed === 6);
            this.$emit('status', this.completed, this.item);
        }
    },

    mounted() {
        if (!this.data.dcIsolators) {
            this.data.dcIsolators = [];
            this.$emit('update:data', this.data);
        }
    },

    methods: {
        generateDcIsolators(number) {
            if (number && number !== 0) {
                const dcIsolators = [];

                [...Array(Number(this.data.dcIsolatorsNum))].map(() => {
                   dcIsolators.push({
                        dcManifacturer: '',
                        dcModel: '',
                        dcQty: '',
                    })
                })

                this.data.dcIsolators = dcIsolators;
                this.$emit('update:data', this.data);
            }
        },

        decimals(value) {
            return Number(value).toFixed(2);
        }
    },
}
</script>
